/**
 * App icons (RFC 0332): one house style, the specimen tile.
 *
 * A square of one of eight colours, the app's letter top left, a two-letter
 * code top right, and a thin line drawing of what the app does. Below 32 px the
 * tile is the letter alone, larger, on the colour, because that is all a tab
 * can hold and it is still enough to tell apps apart.
 *
 * An icon is a small SPEC, not a picture. It is written in the app's
 * `package.json` (`mod.icon`), copied onto the catalog entry by push, and drawn
 * here by one pure function, so the window, the CLI and a test all draw the
 * same bytes. Whatever the spec leaves out is generated from the app: the
 * letter and code from its name, the colour from its id. The id rather than the
 * name or the data type, because both Inbox apps own `mailthread` and the text
 * editor owns no type, but every app id is unique and survives a rename.
 *
 * The style is fixed on purpose: an app picks among the eight colours and the
 * mark set, and cannot bring its own. That is what keeps apps written by
 * different people one family. A custom mark is only a path on the 24 grid,
 * drawn in the same hairline, so there is no free SVG to sanitise.
 */

/** One app's tile. Every field optional: what is missing is generated. */
export interface IconSpec {
  /** One or two characters, top left. Default: the name's first letter. */
  letter?: string
  /** Two characters, top right. Default: derived from the name. */
  code?: string
  /** One of the eight house colours. Default: a hash of the app id. */
  color?: IconColor
  /** A mark from the set, or a path drawn in Vector on the 24 grid. */
  mark?: MarkName | { path: string }
}

export const ICON_COLORS = [
  'red',
  'yellow',
  'blue',
  'green',
  'black',
  'cream',
  'grey',
  'pink',
] as const
export type IconColor = (typeof ICON_COLORS)[number]

/** Each colour's tile and the ink drawn on it. */
const PALETTE: Record<IconColor, { tile: string; ink: string }> = {
  red: { tile: '#F0401F', ink: '#111111' },
  yellow: { tile: '#FFC21A', ink: '#111111' },
  blue: { tile: '#3B7CF3', ink: '#FFFFFF' },
  green: { tile: '#1E8A3C', ink: '#FFFFFF' },
  black: { tile: '#1C1C1C', ink: '#F2F2F2' },
  cream: { tile: '#F4EAD6', ink: '#D2381B' },
  grey: { tile: '#A9A9A9', ink: '#111111' },
  pink: { tile: '#F6A9C6', ink: '#D2381B' },
}

/** The light tiles, which need a hairline edge to hold on a light sidebar. */
const LIGHT_TILES = new Set<IconColor>(['yellow', 'cream', 'grey', 'pink'])

/**
 * The mark set, on a 24 grid, drawn as outlines in one hairline.
 *
 * Built only from circles, rounded rects and single strokes, so every mark
 * looks drawn by one hand. The first ones are the marks drawn for the apps
 * installed when this landed (RFC 0332's design page); more are added the same
 * way, never as free drawings.
 */
export const MARKS = {
  circles:
    '<circle cx="10" cy="10.5" r="7"/><circle cx="15.5" cy="14.5" r="5.5"/>',
  check:
    '<rect x="3" y="3" width="18" height="18" rx="4.5"/><path d="M7.5 12.3l3 3 6-6.6"/>',
  slides:
    '<rect x="3" y="5" width="18" height="14" rx="2.5"/><rect x="6" y="8.5" width="6" height="7" rx="1.5"/>',
  target:
    '<circle cx="12" cy="12" r="9"/><circle cx="12" cy="12" r="5.8"/><circle cx="12" cy="12" r="2.4"/>',
  tray: '<path d="M3 12h5.5l1.5 3h4l1.5-3H21v6.5a2.5 2.5 0 0 1-2.5 2.5h-13A2.5 2.5 0 0 1 3 18.5z"/><circle cx="12" cy="6" r="3"/>',
  envelope:
    '<rect x="3" y="6" width="18" height="13" rx="2.5"/><path d="M5.5 8.5 12 13.5l6.5-5"/>',
  house:
    '<path d="M12 3 21.5 11.5H2.5z"/><rect x="4.5" y="10" width="15" height="11" rx="2"/><rect x="10" y="14.5" width="4" height="6.5" rx="1.5"/>',
  week: '<rect x="3" y="9" width="3" height="11" rx="1.5"/><rect x="7.5" y="6" width="3" height="14" rx="1.5"/><rect x="12" y="11" width="3" height="9" rx="1.5"/><rect x="16.5" y="13" width="3" height="7" rx="1.5"/>',
  shapes:
    '<circle cx="7.5" cy="7.5" r="4"/><rect x="13" y="3.5" width="7.5" height="7.5" rx="1.5"/><path d="M3.5 20.5 7.5 13.5l4 7z"/><rect x="13" y="13" width="7.5" height="7.5" rx="3.75"/>',
  nib: '<path d="M4 20A16 16 0 0 1 20 4"/><rect x="2" y="17.5" width="5" height="5" rx="1"/><rect x="17" y="1.5" width="5" height="5" rx="1"/>',
  timeline:
    '<rect x="3" y="5" width="9" height="3.5" rx="1.75"/><rect x="7" y="10.25" width="14" height="3.5" rx="1.75"/><rect x="3" y="15.5" width="8" height="3.5" rx="1.75"/><path d="M15.3 3v18"/>',
  people:
    '<circle cx="8.5" cy="8" r="3.5"/><path d="M2.5 20a6 6 0 0 1 12 0z"/><circle cx="16.5" cy="9" r="3"/><path d="M14.5 14.3a4.3 4.3 0 0 1 6.3 5.7"/>',
  calendar:
    '<rect x="3" y="5" width="18" height="16" rx="2.5"/><path d="M8.3 2.5v4.5M15.7 2.5v4.5M3 10h18"/><rect x="14.8" y="13" width="3.2" height="3.2" rx=".8"/>',
  flask:
    '<path d="M9 3h6v6l5.2 9.5A2.3 2.3 0 0 1 18.2 22H5.8a2.3 2.3 0 0 1-2-3.5L9 9z"/><path d="M6.8 15h10.4"/>',
  bars: '<rect x="4" y="12" width="4" height="9" rx="1.5"/><rect x="10" y="4" width="4" height="17" rx="1.5"/><rect x="16" y="8.5" width="4" height="12.5" rx="1.5"/>',
  brackets:
    '<path d="M8 6.5 2.8 12 8 17.5M16 6.5l5.2 5.5-5.2 5.5M13.6 4.5 10.4 19.5"/>',
  columns:
    '<rect x="3.5" y="4" width="7.5" height="16" rx="2.5"/><rect x="13" y="4" width="7.5" height="16" rx="2.5"/><path d="M13 11.5h7.5"/>',
  lines: '<path d="M4 6.5h16M4 12h16M4 17.5h9"/><path d="M17 15v5"/>',
  face: '<circle cx="12" cy="12" r="9"/><path d="M9.3 9v3M14.7 9v3"/><circle cx="12" cy="16.2" r="1.8"/>',
} as const
export type MarkName = keyof typeof MARKS

/** The app an icon is drawn for: enough to generate what the spec leaves out. */
export interface IconApp {
  id: string
  name: string
  /** Picks the house icon for a first-party app that set none (see HOUSE_ICONS). */
  dataType?: string
  /** What the app says it does; its words pick a drawing (see MARK_WORDS). */
  description?: string
}

/** A resolved tile: every field filled in, ready to draw. */
export interface ResolvedIcon {
  letter: string
  code: string
  color: IconColor
  mark?: MarkName | { path: string }
}

/** FNV-1a, so the same id picks the same colour everywhere, in every build. */
function hash(text: string): number {
  let h = 2166136261 >>> 0
  for (let i = 0; i < text.length; i++) {
    h ^= text.charCodeAt(i)
    h = Math.imul(h, 16777619)
  }
  return h >>> 0
}

/** The name's letters, for the letter and code to be taken from. */
function letters(name: unknown): string {
  // THROUGH `String`, because a stored name is not always a JS string. An app
  // name written since RFC 0356, or carried through a project move, is an
  // Automerge `ImmutableString`: an object with no `.replace`. Calling it
  // threw inside `AppIconsProvider`, which took the whole window down to a
  // blank page, because an icon helper is not somewhere anyone has an error
  // boundary.
  return String(name ?? '')
    .replace(/[^a-z0-9]/gi, '')
    .toUpperCase()
}

/** The default letter: the name's first letter or digit. */
export function generatedLetter(name: unknown): string {
  return letters(name).charAt(0) || '?'
}

/**
 * The code candidates for a name, best first.
 *
 * First letter plus each later consonant ("Chat" CH, "Calendar" CL, "Canvas"
 * CN), then first letter plus each later letter, then first letter plus a digit.
 * The first is the default; the rest are what `distinctCodes` falls back to
 * when two apps in one list would otherwise share a code.
 */
function codeCandidates(name: string): string[] {
  const all = letters(name)
  const first = all.charAt(0) || 'X'
  const rest = all.slice(1)
  const out: string[] = []
  const add = (c: string) => {
    if (!out.includes(c)) out.push(c)
  }
  for (const ch of rest) if (/[B-DF-HJ-NP-TV-Z]/.test(ch)) add(first + ch)
  for (const ch of rest) add(first + ch)
  for (let d = 1; d <= 9; d++) add(first + String(d))
  return out
}

/** The default code for one app, from its name alone. */
export function generatedCode(name: string): string {
  return codeCandidates(name)[0] ?? 'XX'
}

/** The default colour, from the app's id. */
export function generatedColor(id: string): IconColor {
  return ICON_COLORS[hash(id) % ICON_COLORS.length] as IconColor
}

/**
 * Codes for a whole list of apps, no two the same.
 *
 * An app that set its code keeps it. The rest take their first free candidate,
 * in list order, so the same catalog always gives the same codes. A surface
 * that draws several apps at once (the packages list, the sidebar) calls this
 * once for the catalog rather than generating each code alone.
 */
export function distinctCodes(
  apps: Array<IconApp & { icon?: IconSpec }>,
): Map<string, string> {
  const out = new Map<string, string>()
  const taken = new Set<string>()
  for (const app of apps) {
    const set = app.icon?.code
    if (set) {
      out.set(app.id, set)
      taken.add(set)
    }
  }
  for (const app of apps) {
    if (out.has(app.id)) continue
    const code =
      codeCandidates(app.name).find((c) => !taken.has(c)) ??
      generatedCode(app.name)
    out.set(app.id, code)
    taken.add(code)
  }
  return out
}

/** Fill in what a spec leaves out. `code` overrides the generated code. */
/**
 * The icons drawn for the apps Mod ships (RFC 0332's design page, direction D),
 * used for any field an app has not set itself.
 *
 * Keyed by data type, which a first-party app keeps for life, and by name for
 * the two that own no type. An app's own `mod.icon` always wins, field by
 * field, so this is only what a shipped app looks like before anybody chooses.
 * A third-party app is not in here and gets the generated tile.
 */
export const HOUSE_ICONS: Readonly<Record<string, IconSpec>> = {
  review: { color: 'black', mark: 'columns' },
  chatchannel: { color: 'blue', mark: 'circles' },
  deck: { color: 'red', mark: 'slides' },
  evaluation: { color: 'grey', mark: 'target' },
  mailthread: { color: 'yellow', mark: 'envelope' },
  household: { color: 'green', mark: 'house' },
  week: { color: 'pink', mark: 'week' },
  canvas: { color: 'cream', mark: 'shapes' },
  vector: { color: 'black', mark: 'nib' },
  videoedit: { color: 'red', mark: 'timeline' },
  crmrecord: { color: 'green', mark: 'people' },
  calevent: { color: 'grey', mark: 'calendar' },
  'tasks-app': { color: 'yellow', mark: 'check' },
  openprobe: { color: 'black', mark: 'flask' },
  hellonote: { color: 'pink', mark: 'face' },
  poll: { color: 'blue', mark: 'bars' },
  iconset: { color: 'black', mark: 'shapes' },
  'name:Text editor': { color: 'cream', mark: 'lines' },
  'name:Code': { color: 'black', mark: 'brackets' },
}

/** The house icon for an app, if it is one Mod ships. */
export function houseIcon(app: IconApp): IconSpec | undefined {
  return (
    (app.dataType ? HOUSE_ICONS[app.dataType] : undefined) ??
    HOUSE_ICONS[`name:${app.name}`]
  )
}

/**
 * The words that pick each drawing, for an app Mod does not ship.
 *
 * Matched against the app's name (counted twice), its data type and its
 * description, so "Standup notes" draws lines and "Team chat" draws two
 * circles. Nothing matching means no drawing, which is honest: a random
 * drawing would look like a decision nobody made.
 */
export const MARK_WORDS: Readonly<Record<MarkName, readonly string[]>> = {
  circles: ['chat', 'message', 'talk', 'conversation', 'channel', 'dm'],
  check: ['task', 'todo', 'to-do', 'checklist', 'done'],
  slides: ['deck', 'slide', 'presentation', 'present', 'pitch'],
  target: ['eval', 'score', 'grade', 'goal', 'metric', 'okr'],
  tray: ['inbox', 'triage', 'queue', 'agent'],
  envelope: ['mail', 'email', 'letter', 'newsletter'],
  house: ['home', 'house', 'household', 'family'],
  week: ['week', 'weekly', 'plan', 'planner', 'habit'],
  shapes: ['canvas', 'board', 'whiteboard', 'diagram', 'shape'],
  nib: ['vector', 'logo', 'pen', 'draw', 'design', 'icon'],
  timeline: ['video', 'film', 'clip', 'cut', 'edit', 'timeline'],
  people: ['crm', 'contact', 'people', 'team', 'customer', 'deal'],
  calendar: ['calendar', 'event', 'schedule', 'meeting', 'booking'],
  flask: ['probe', 'lab', 'experiment', 'test', 'sandbox'],
  bars: ['poll', 'vote', 'survey', 'chart', 'analytics', 'stats'],
  brackets: ['code', 'source', 'script', 'dev', 'api'],
  columns: ['review', 'diff', 'compare', 'kanban'],
  lines: ['note', 'notes', 'text', 'doc', 'writing', 'journal', 'markdown'],
  face: ['hello', 'profile', 'avatar', 'mood'],
}

/** The drawing an app's own words point at, or none. */
export function markFromWords(app: IconApp): MarkName | undefined {
  // `String` first: an app's name or description read off a document can be
  // an Automerge `ImmutableString`, which has no string methods. Every value
  // reaching this module comes from a document, so each entry point coerces
  // rather than trusting its declared type.
  const words = (text: unknown) =>
    String(text ?? '')
      .toLowerCase()
      .split(/[^a-z0-9-]+/)
      .filter(Boolean)
  const name = words(app.name)
  const rest = [...words(app.dataType), ...words(app.description)]
  let best: MarkName | undefined
  let bestScore = 0
  for (const [mark, keys] of Object.entries(MARK_WORDS) as Array<
    [MarkName, readonly string[]]
  >) {
    let score = 0
    for (const k of keys) {
      score += 2 * name.filter((w) => w === k || w === `${k}s`).length
      score += rest.filter((w) => w === k || w === `${k}s`).length
    }
    // Ties go to the earlier mark, so the answer never depends on map order.
    if (score > bestScore) {
      best = mark
      bestScore = score
    }
  }
  return best
}

/**
 * A complete icon for an app: what it set, with the rest filled in.
 *
 * Deterministic, so storing it and drawing it unstored give the same tile.
 * The code is left out on purpose: codes are made distinct across a whole
 * catalog (`distinctCodes`), which one app on its own cannot know.
 */
export function generateIcon(
  app: IconApp,
  spec?: IconSpec,
): IconSpec & { letter: string; color: IconColor } {
  const house = houseIcon(app)
  const mark = spec?.mark ?? house?.mark ?? markFromWords(app)
  return {
    letter: spec?.letter || house?.letter || generatedLetter(app.name),
    ...(spec?.code || house?.code ? { code: spec?.code || house?.code } : {}),
    color: spec?.color ?? house?.color ?? generatedColor(app.id),
    ...(mark ? { mark } : {}),
  }
}

/** Fill in what a spec leaves out, for drawing. `code` is the catalog's. */
export function resolveIcon(
  spec: IconSpec | undefined,
  app: IconApp,
  code?: string,
): ResolvedIcon {
  const icon = generateIcon(app, spec)
  return {
    letter: icon.letter,
    code: spec?.code || code || icon.code || generatedCode(app.name),
    color: icon.color,
    ...(icon.mark ? { mark: icon.mark } : {}),
  }
}

/**
 * A new balanced set for a whole catalog: a person's own icons.
 *
 * Colours spread, each app taking the least-used colour that differs from the
 * app before it, starting from a colour picked by `seed` so a second call with
 * a new seed gives a different set. Codes are made distinct. Letters and
 * drawings are kept: they say what each app is, and a set is a look, not a
 * renaming.
 */
export function restyleIcons(
  apps: ReadonlyArray<IconApp & { icon?: IconSpec }>,
  seed = '',
): Map<string, IconSpec> {
  const used = new Map<IconColor, number>(ICON_COLORS.map((c) => [c, 0]))
  const codes = distinctCodes(apps.map((a) => ({ id: a.id, name: a.name })))
  const out = new Map<string, IconSpec>()
  let previous: IconColor | undefined
  for (const app of apps) {
    const base = generateIcon(app, app.icon)
    const start = seed
      ? (ICON_COLORS[
          hash(`${seed}:${app.id}`) % ICON_COLORS.length
        ] as IconColor)
      : base.color
    const order = [start, ...ICON_COLORS.filter((c) => c !== start)]
    const least = Math.min(
      ...order.filter((c) => c !== previous).map((c) => used.get(c) ?? 0),
    )
    const color =
      order.find((c) => c !== previous && (used.get(c) ?? 0) === least) ?? start
    used.set(color, (used.get(color) ?? 0) + 1)
    previous = color
    out.set(app.id, cleanIconSpec({ ...base, color, code: codes.get(app.id) }))
  }
  return out
}

// Takes `unknown`, because a value off a document is not always a JS string:
// an `ImmutableString` has no string methods, and this is the one place every
// piece of icon text passes through on its way into the SVG.
const escapeXml = (text: unknown) =>
  String(text ?? '').replace(/[<>&"']/g, (c) =>
    c === '<'
      ? '&lt;'
      : c === '>'
        ? '&gt;'
        : c === '&'
          ? '&amp;'
          : c === '"'
            ? '&quot;'
            : '&apos;',
  )

const MONO = "ui-monospace, 'SF Mono', Menlo, monospace"

/**
 * The tile as SVG, on a 64 viewBox with no width or height: whoever shows it
 * sets the size. `px` is the size it will be SHOWN at, because the tile changes
 * with size: at 32 and up it draws letter, code and mark; below 32 the letter
 * alone. Text is drawn in the SVG, so the tile needs no font files.
 */
export function appIcon(
  spec: IconSpec | undefined,
  app: IconApp,
  px: number,
  code?: string,
): string {
  const icon = resolveIcon(spec, app, code)
  const { tile, ink } = PALETTE[icon.color]
  // A hairline edge where the tile would otherwise dissolve: dark on the light
  // tiles (for a light sidebar), light on the black one (for a dark one).
  const edge = LIGHT_TILES.has(icon.color)
    ? '<rect x=".4" y=".4" width="63.2" height="63.2" rx="8.6" fill="none" stroke="#000" stroke-opacity=".08" stroke-width=".8"/>'
    : icon.color === 'black'
      ? '<rect x=".4" y=".4" width="63.2" height="63.2" rx="8.6" fill="none" stroke="#fff" stroke-opacity=".14" stroke-width=".8"/>'
      : ''
  const letter = escapeXml(String(icon.letter ?? '').slice(0, 2))
  const open = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64"><rect width="64" height="64" rx="9" fill="${tile}"/>`
  const markBody = !icon.mark
    ? ''
    : typeof icon.mark === 'string'
      ? (MARKS[icon.mark] ?? '')
      : isValidMarkPath(icon.mark.path)
        ? `<path d="${icon.mark.path}"/>`
        : ''
  // A tab or a sidebar row: the drawing alone, centred on its colour, with a
  // heavy pen so it survives 14 px. Six apps start with C, so a letter says
  // less here than the drawing does. An app with no drawing shows its letter.
  if (px < 24) {
    if (markBody) {
      const ks = (64 * 0.66) / 24
      const off = (64 - 24 * ks) / 2
      return `${open}<g transform="translate(${off.toFixed(2)} ${off.toFixed(2)}) scale(${ks.toFixed(3)})" fill="none" stroke="${ink}" stroke-width="${(4.2 / ks).toFixed(3)}" stroke-linecap="round" stroke-linejoin="round">${markBody}</g>${edge}</svg>`
    }
    return `${open}<text x="32" y="33" text-anchor="middle" dominant-baseline="central" font-family="${MONO}" font-weight="600" font-size="${letter.length > 1 ? 30 : 40}" fill="${ink}">${letter}</text>${edge}</svg>`
  }
  // The mark in the bottom-right corner, stroked at a constant weight in tile
  // units so every mark reads as the same pen. At row size (24 to 47 px) the
  // mark is larger and the pen heavier, and the code is left out: at that size
  // a 6.5-unit code is two pixels high and says nothing.
  const row = px < 48
  const k = (64 * (row ? 0.5 : 0.42)) / 24
  const at = 64 - (row ? 6 : 7) - 24 * k
  const pen = row ? 2.4 : 1.1
  const mark = markBody
    ? `<g transform="translate(${at.toFixed(2)} ${at.toFixed(2)}) scale(${k.toFixed(3)})" fill="none" stroke="${ink}" stroke-width="${(pen / k).toFixed(3)}" stroke-linecap="round" stroke-linejoin="round">${markBody}</g>`
    : ''
  if (row) {
    return `${open}<text x="7" y="${markBody ? 27 : 44}" font-family="${MONO}" font-weight="600" font-size="${markBody ? 26 : 36}" fill="${ink}">${letter}</text>${mark}${edge}</svg>`
  }
  return `${open}<text x="7" y="18" font-family="${MONO}" font-weight="500" font-size="14" fill="${ink}">${letter}</text><text x="57" y="13" text-anchor="end" font-family="${MONO}" font-size="6.5" fill="${ink}">${escapeXml(icon.code.slice(0, 2))}</text>${mark}${edge}</svg>`
}

/** The tile as a data URL, for an `<img>`. */
export function appIconUrl(
  spec: IconSpec | undefined,
  app: IconApp,
  px: number,
  code?: string,
): string {
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(appIcon(spec, app, px, code))}`
}

/** The most a custom mark may hold. The drawn marks are under 300 bytes. */
const MAX_PATH = 2048

/**
 * Is this a path that may be drawn as a mark?
 *
 * Path commands and numbers only, under 2 KB, and every point it reaches
 * inside the 24 box (with half a unit of slack for the stroke). A mark is a
 * drawing in the house hairline, not a way in for anything else, so the check
 * is on what the string IS rather than on what it might do.
 */
export function isValidMarkPath(path: string): boolean {
  if (typeof path !== 'string' || path.length === 0 || path.length > MAX_PATH)
    return false
  if (!/^[MmLlHhVvCcSsQqTtAaZz0-9eE.,\s+-]+$/.test(path)) return false
  if (!/^\s*[Mm]/.test(path)) return false
  const tokens = path.match(
    /[A-Za-z]|[-+]?(?:\d+\.?\d*|\.\d+)(?:[eE][-+]?\d+)?/g,
  )
  if (!tokens) return false
  const arity: Record<string, number> = {
    M: 2,
    L: 2,
    H: 1,
    V: 1,
    C: 6,
    S: 4,
    Q: 4,
    T: 2,
    A: 7,
    Z: 0,
  }
  let x = 0
  let y = 0
  let startX = 0
  let startY = 0
  let cmd = ''
  let i = 0
  const inside = (px: number, py: number) =>
    px >= -0.5 && px <= 24.5 && py >= -0.5 && py <= 24.5
  while (i < tokens.length) {
    const t = tokens[i] as string
    if (/[A-Za-z]/.test(t)) {
      cmd = t
      i++
      if (cmd === 'Z' || cmd === 'z') {
        x = startX
        y = startY
      }
      continue
    }
    const upper = cmd.toUpperCase()
    const n = arity[upper]
    if (n === undefined || n === 0) return false
    const args = tokens.slice(i, i + n).map(Number)
    if (args.length < n || args.some((v) => !Number.isFinite(v))) return false
    i += n
    const rel = cmd !== upper
    // Every point a segment names (endpoint and control points) must be inside.
    const pts: Array<[number, number]> = []
    if (upper === 'H')
      pts.push([rel ? x + (args[0] as number) : (args[0] as number), y])
    else if (upper === 'V')
      pts.push([x, rel ? y + (args[0] as number) : (args[0] as number)])
    else if (upper === 'A') {
      const ex = args[5] as number
      const ey = args[6] as number
      pts.push(rel ? [x + ex, y + ey] : [ex, ey])
    } else {
      for (let j = 0; j < n; j += 2) {
        const ax = args[j] as number
        const ay = args[j + 1] as number
        pts.push(rel ? [x + ax, y + ay] : [ax, ay])
      }
    }
    for (const [px, py] of pts) if (!inside(px, py)) return false
    const end = pts[pts.length - 1] as [number, number]
    x = end[0]
    y = end[1]
    if (upper === 'M') {
      startX = x
      startY = y
      // Further pairs after a moveto are linetos, as SVG reads them.
      cmd = rel ? 'l' : 'L'
    }
  }
  return true
}

/**
 * What is wrong with an icon spec, or null when it may be written.
 *
 * Checked where an icon is WRITTEN (push, `update_app`), so a bad one is
 * refused with a reason rather than drawn wrong later.
 */
export function iconSpecError(spec: unknown): string | null {
  if (spec === undefined) return null
  if (!spec || typeof spec !== 'object' || Array.isArray(spec))
    return 'icon must be an object'
  const s = spec as Record<string, unknown>
  for (const key of Object.keys(s)) {
    if (!['letter', 'code', 'color', 'mark'].includes(key))
      return `icon has an unknown field "${key}"`
  }
  if (
    s.letter !== undefined &&
    (typeof s.letter !== 'string' || s.letter.length < 1 || s.letter.length > 2)
  )
    return 'icon.letter must be one or two characters'
  if (
    s.code !== undefined &&
    (typeof s.code !== 'string' || s.code.length < 1 || s.code.length > 2)
  )
    return 'icon.code must be one or two characters'
  if (s.color !== undefined && !ICON_COLORS.includes(s.color as IconColor))
    return `icon.color must be one of ${ICON_COLORS.join(', ')}`
  if (s.mark !== undefined) {
    if (typeof s.mark === 'string') {
      if (!(s.mark in MARKS))
        return `icon.mark "${s.mark}" is not in the mark set (${Object.keys(MARKS).join(', ')})`
    } else if (
      !s.mark ||
      typeof s.mark !== 'object' ||
      typeof (s.mark as { path?: unknown }).path !== 'string' ||
      Object.keys(s.mark).length !== 1
    ) {
      return 'icon.mark must be a mark name or { "path": "..." }'
    } else if (!isValidMarkPath((s.mark as { path: string }).path)) {
      return 'icon.mark.path must be path commands only, inside the 24 box, under 2 KB'
    }
  }
  return null
}

/** A spec as stored: only the fields that are set, so a diff stays small. */
export function cleanIconSpec(spec: IconSpec): IconSpec {
  return {
    ...(spec.letter ? { letter: spec.letter } : {}),
    ...(spec.code ? { code: spec.code } : {}),
    ...(spec.color ? { color: spec.color } : {}),
    ...(spec.mark
      ? {
          mark:
            typeof spec.mark === 'string'
              ? spec.mark
              : { path: spec.mark.path },
        }
      : {}),
  }
}
